import type { ReactNode } from 'react'
import toast from 'react-hot-toast'
import { useForge, useResearchForge, useMyFleet, type ForgeTechDto, type ResourcesDto, type ForgeActiveResearch } from '@/api/hooks'
import { useCountdown } from '@/hooks/useCountdown'
import { formatDuration } from '@/lib/time'
import {
  ForgeIcon,
  ForgeBranchIcon,
  MapIcon,
  CompassIcon,
  WatchtowerIcon,
  BuildingIcon,
  GoldIcon,
  WoodIcon,
  RumIcon,
  CheckIcon,
  StatIcon,
  type StatName,
} from '@/components/game/GameIcons'
import { GoldButton } from '@/components/game/ui'
import { SeaScene } from '@/components/game/SeaScene'

/**
 * /forge — the Forge tech tree. Spend gold + wood + rhum (the main rhum sink) to
 * unlock ship modules, per-round combat rerolls, hull reinforcement and
 * logistics edges. Tiered upgrades collapse into a single card (like the
 * island's per-level building forecast); branches can't all be afforded in a
 * season, so the choice shapes the captain's fleet.
 */
export function Forge() {
  const { data, isLoading, isError, error } = useForge()
  const { data: fleet } = useMyFleet()
  const research = useResearchForge()

  if (isLoading) {
    return <div className="max-w-5xl mx-auto p-6 text-ocean-100">Chargement de la Forge…</div>
  }
  if (isError || !data) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <p className="text-rum-500">Forge indisponible : {(error as Error)?.message}</p>
      </div>
    )
  }

  const resources = fleet?.resources
  const branches = groupByBranch(data.techs)
  const active = data.activeResearch

  const onResearch = (tech: ForgeTechDto) => {
    research.mutate(tech.key, {
      onSuccess: () => toast.success(`Recherche lancée : ${tech.label}`),
      onError: (err) => toast.error(err.message || 'Recherche impossible'),
    })
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 pb-[300px] space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-sand-300 flex items-center gap-3">
          <ForgeIcon className="w-8 h-8 text-sand-300" aria-hidden="true" /> La Forge
        </h1>
        <p className="text-sm text-ocean-200 mt-1">
          Dépense or, bois et rhum pour débloquer modules, relances de combat, coques renforcées et
          avantages logistiques. Les améliorations à paliers se débloquent niveau par niveau ; tu ne
          pourras pas tout financer en une saison.
        </p>
        <p className="text-xs text-ocean-300 mt-1">
          <strong className="text-sand-300">{data.researchedKeys.length}</strong> / {data.techs.length} technologies débloquées
        </p>
      </div>

      {branches.map(({ branch, label, techs }) => (
        <section key={branch}>
          <h2 className="font-display text-lg text-sand-100 mb-3 flex items-center gap-2">
            <ForgeBranchIcon branch={branch} /> {label}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {buildLines(techs).map((line) => (
              <LineCard
                key={line.key}
                line={line}
                resources={resources}
                pending={research.isPending}
                active={active}
                onResearch={onResearch}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Integrated maritime backdrop — a fleet at sail, always present at the foot of the page. */}
      <SeaScene scene="map" />
    </div>
  )
}

function LineCard({
  line,
  resources,
  pending,
  active,
  onResearch,
}: {
  line: ForgeLine
  resources: ResourcesDto | null | undefined
  pending: boolean
  active: ForgeActiveResearch | null
  onResearch: (tech: ForgeTechDto) => void
}) {
  const { tiers, researchedCount, next } = line
  const multi = tiers.length > 1
  const shown = next ?? tiers[tiers.length - 1]
  const thisTierInProgress = !!next && next.inProgress
  const busyElsewhere = !!active && !thisTierInProgress
  const countdown = useCountdown(active?.resolvesAt ?? null)
  const affordable =
    !!resources &&
    !!next &&
    resources.gold >= next.cost.gold &&
    resources.wood >= next.cost.wood &&
    resources.rum >= next.cost.rhum

  return (
    <div className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/40 p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display text-lg text-sand-300 flex items-center gap-2">
          {effectIcon(line.icon)}
          {line.label}
        </h3>
        {multi && (
          <span className="shrink-0 text-xs font-semibold bg-ocean-900/60 text-sand-100 rounded px-2 py-1">
            Niv. {researchedCount}/{tiers.length}
          </span>
        )}
      </div>

      <p className="text-xs text-ocean-300 flex-1">{shown.description}</p>

      <div className="mt-auto pt-2 border-t border-sand-500/12 space-y-2">
        {next && !thisTierInProgress && (
          <>
            <div className="flex items-center gap-3 text-xs text-ocean-100">
              <Cost icon={<GoldIcon className="w-3.5 h-3.5 text-sand-300" />} value={next.cost.gold} have={resources?.gold} />
              <Cost icon={<WoodIcon className="w-3.5 h-3.5 text-sand-300" />} value={next.cost.wood} have={resources?.wood} />
              <Cost icon={<RumIcon className="w-3.5 h-3.5 text-sand-300" />} value={next.cost.rhum} have={resources?.rum} />
            </div>
            <p className="flex items-center gap-1 text-[11px] text-ocean-300">
              <span aria-hidden="true">⏱</span> {formatDuration(next.durationSeconds)} de recherche
            </p>
          </>
        )}

        {!next ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-300">
            <CheckIcon className="w-4 h-4" /> {multi ? `Max · niveau ${tiers.length}` : 'Débloqué'}
          </span>
        ) : thisTierInProgress ? (
          <div className="bg-ocean-900/55 rounded-lg p-2 text-center border border-sand-500/10">
            <p className="text-[11px] text-ocean-300 uppercase tracking-wider">Recherche en cours</p>
            <p className="font-display text-base text-sand-100 tabular-nums">{countdown}</p>
          </div>
        ) : !next.available ? (
          <span className="block text-xs text-ocean-400 italic">
            {busyElsewhere ? 'Forge occupée' : 'Prérequis manquant'}
          </span>
        ) : (
          <GoldButton
            onClick={() => onResearch(next)}
            disabled={pending || !affordable}
            title={affordable ? 'Lancer la recherche' : 'Ressources insuffisantes'}
            className="w-full text-sm px-3 py-1.5"
          >
            {affordable ? (multi ? `Rechercher niv. ${researchedCount + 1}` : 'Rechercher') : 'Ressources insuffisantes'}
          </GoldButton>
        )}
      </div>
    </div>
  )
}

function Cost({ icon, value, have }: { icon: ReactNode; value: number; have: number | undefined }) {
  const enough = have === undefined || have >= value
  return (
    <span className={`inline-flex items-center gap-1 ${enough ? '' : 'text-red-400'}`}>
      {icon}
      {value.toLocaleString('fr-FR')}
    </span>
  )
}

// ---------- grouping + icons ----------

interface ForgeLine {
  key: string
  label: string
  icon: string
  tiers: ForgeTechDto[]
  researchedCount: number
  /** First not-yet-researched tier (null when maxed). */
  next: ForgeTechDto | null
}

/** Group techs by branch, preserving the catalog's branch order. */
function groupByBranch(techs: ForgeTechDto[]): { branch: string; label: string; techs: ForgeTechDto[] }[] {
  const order: string[] = []
  const byBranch = new Map<string, ForgeTechDto[]>()

  for (const tech of techs) {
    if (!byBranch.has(tech.branch)) {
      byBranch.set(tech.branch, [])
      order.push(tech.branch)
    }
    byBranch.get(tech.branch)!.push(tech)
  }

  return order.map((branch) => ({
    branch,
    label: byBranch.get(branch)![0].branchLabel,
    techs: byBranch.get(branch)!,
  }))
}

/** Collapse `requires`-chains into single tiered lines (T1 → T2 → …). */
function buildLines(techs: ForgeTechDto[]): ForgeLine[] {
  const heads = techs.filter((t) => t.requires === null)

  return heads.map((head) => {
    const tiers = [head]
    let lastKey = head.key
    let nextTier = techs.find((t) => t.requires === lastKey)
    while (nextTier) {
      tiers.push(nextTier)
      lastKey = nextTier.key
      nextTier = techs.find((t) => t.requires === lastKey)
    }

    return {
      key: head.key,
      label: tiers.length > 1 ? head.label.replace(/\s+[IVX]+$/, '') : head.label,
      icon: head.icon,
      tiers,
      researchedCount: tiers.filter((t) => t.researched).length,
      next: tiers.find((t) => !t.researched) ?? null,
    }
  })
}

const STAT_NAMES = new Set<StatName>(['manoeuvre', 'bordee', 'abordage', 'armure', 'hp', 'cargo'])

/** The characteristic icon a tech improves (combat stats, or a logistics glyph). */
function effectIcon(icon: string, className = 'w-5 h-5 text-sand-300'): ReactNode {
  if (STAT_NAMES.has(icon as StatName)) {
    return <StatIcon name={icon as StatName} className={className} />
  }
  switch (icon) {
    case 'build_time':
      return <BuildingIcon type="shipyard" className={className} />
    case 'explore_reward':
      return <MapIcon className={className} />
    case 'travel_speed':
      return <CompassIcon className={className} />
    case 'vigie':
      // mod_spyglass (Longue-vue) — recon depth for the PvP scout. The watchtower
      // glyph reads as "vigie/guet" and ties it visually to the recon contest.
      return <WatchtowerIcon className={className} />
    default:
      return <ForgeIcon className={className} />
  }
}

